import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateTemplateBody,
  type DeprecateTemplateBody,
  type TemplateListParams,
  adminClient,
} from '../lib/admin-client.js';

export const TEMPLATE_KEYS = {
  all: ['admin-templates'] as const,
  list: (params: TemplateListParams) => ['admin-templates', 'list', params] as const,
  detail: (id: string) => ['admin-templates', 'detail', id] as const,
};

export function useAdminTemplates(params: TemplateListParams = {}) {
  return useQuery({
    queryKey: TEMPLATE_KEYS.list(params),
    queryFn: () => adminClient.listTemplates(params),
    staleTime: 30_000,
  });
}

export function useAdminTemplate(templateId: string) {
  return useQuery({
    queryKey: TEMPLATE_KEYS.detail(templateId),
    queryFn: () => adminClient.getTemplate(templateId),
    enabled: templateId.length > 0,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTemplateBody) => adminClient.createTemplate(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEMPLATE_KEYS.all });
    },
  });
}

export function useDeprecateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, body }: { templateId: string; body?: DeprecateTemplateBody }) =>
      adminClient.deprecateTemplate(templateId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: TEMPLATE_KEYS.all });
      void qc.invalidateQueries({ queryKey: TEMPLATE_KEYS.detail(vars.templateId) });
    },
  });
}

export function useValidateTemplate() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => adminClient.validateTemplate(body),
  });
}
